import { useCallback, useRef, useState } from 'react'
import { ActionCardModel } from '@playbooks/action-cards-utils'
import type { ActionCardsElement } from '@playbooks/action-cards'
import { Question, QuestionTypes } from '@playbooks/core/src/interfaces/ActionCard'

import { EMAIL_REG_EXP } from '../constants/index'
import { saveLocalData } from '../services/storage'
import useMountEffect from './useMountEffect'

function useEmailControl({
  loggedInUserEmail,
  actionCardsElementRef,
  storageId,
}: {
  loggedInUserEmail: string | null
  actionCardsElementRef: React.RefObject<ActionCardsElement | null>
  storageId: string
}) {
  const [isPolicyAccepted, setIsPolicyAccepted] = useState(!!loggedInUserEmail)
  const [webPlayEmail, setWebPlayEmail] = useState<string | null>(null)
  const [isInvalid, setIsInvalid] = useState<boolean>(false)
  const [isWebPlayEmailInputVisible, setIsWebPlayEmailInputVisible] = useState(true)
  const forceWebPlayEmailVisibility = useRef(false)

  const syncWebPlayEmail = useCallback(() => {
    if (loggedInUserEmail && EMAIL_REG_EXP.test(loggedInUserEmail)) {
      setIsWebPlayEmailInputVisible(false)
      setWebPlayEmail(loggedInUserEmail)
      return
    }

    const actionCardData = actionCardsElementRef.current?.getValue()

    if (!actionCardData) return

    const searchService = new ActionCardModel(actionCardData).getSearchService()

    const firstVisibleEmailNode = searchService.filterVisible().find(node => (node.data as Question).type === 'Email')
    const firstAnyEmailNode = searchService.find(node => (node.data as Question).type === 'Email')

    const firstVisibleEmailNodeWithValue = searchService
      .filterVisible()
      .find(node => (node.data as Question).type === 'Email' && !!(node.data as Question).value)

    const firstAnyEmailNodeWithValue = searchService.find(
      node => (node.data as Question).type === 'Email' && !!(node.data as Question).value,
    )

    // Email from an action card
    const emailNode =
      firstVisibleEmailNodeWithValue || firstVisibleEmailNode || firstAnyEmailNodeWithValue || firstAnyEmailNode

    if (!emailNode) {
      // if email node isn't found (more likely isn't added)
      // we shouldn't set webPlay email to null
      // in other cases - value of the email in action card has to be synchronized with the email-input in the popup
      // Also if an action card does not have an email node, we should show the previously entered email
      setWebPlayEmail(email => email || null)
      setIsWebPlayEmailInputVisible(!EMAIL_REG_EXP.test('') || forceWebPlayEmailVisibility.current)
      return
    }

    const email = emailNode.getValue()
    setWebPlayEmail(email)
    setIsWebPlayEmailInputVisible(!EMAIL_REG_EXP.test(email) || forceWebPlayEmailVisibility.current)
  }, [actionCardsElementRef, loggedInUserEmail])

  useMountEffect(() => {
    syncWebPlayEmail()
  })

  return {
    isInvalid,
    webPlayEmail,
    setWebPlayEmail: (email: string) => {
      forceWebPlayEmailVisibility.current = true
      setWebPlayEmail(email)
      if (!email || EMAIL_REG_EXP.test(email)) {
        setIsInvalid(false)
        const ac = actionCardsElementRef.current!.getValue()!
        const searchService = new ActionCardModel(ac).getSearchService()

        const firstVisibleEmailNode = searchService
          .filterVisible()
          .find(node => (node.data as Question<string>).type === QuestionTypes.EMAIL)

        const firstAnyEmailNode = searchService.find(
          node => (node.data as Question<string>).type === QuestionTypes.EMAIL,
        )

        const emailNode = firstVisibleEmailNode || firstAnyEmailNode

        if (!emailNode) return

        const updatedData = actionCardsElementRef.current!.partialUpdate({
          isLocked: !!ac?.isLocked,
          room: '',
          data: {
            [emailNode.data.id]: {
              value: email,
            },
          },
        })

        saveLocalData(updatedData, storageId)
      } else {
        setIsInvalid(true)
      }
    },
    isPolicyAccepted,
    syncWebPlayEmail,
    setIsPolicyAccepted,
    isWebPlayEmailInputVisible,
    isWebPlayEmailValid: webPlayEmail && !!EMAIL_REG_EXP.test(webPlayEmail),
  }
}

export default useEmailControl
